import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  margin: 0 auto;
  overflow: hidden;
  position: relative;
  background-color: black;
  color: white;
  max-width: 390px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  width: 100%;
  background-color: black;
  z-index: 10;
`;

const BackButton = styled.span`
  cursor: pointer;
  color: white;
  font-weight: 500;
  font-size: 16px;
`;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: white;
  margin: 0;
`;

const Content = styled.div`
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ListingCard = styled.div`
  background: #1a1a1a;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #333;
  position: relative;
`;

const ListingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const ListingTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: white;
  flex: 1;
`;

const StatusBadge = styled.span`
  background: ${props => props.active ? '#17A2B8' : '#666'};
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
`;

const ListingMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const Category = styled.span`
  color: #17A2B8;
  font-size: 12px;
  font-weight: 500;
`;

const Price = styled.span`
  color: #34C759;
  font-size: 14px;
  font-weight: 600;
`;

const Description = styled.p`
  font-size: 14px;
  color: #ccc;
  margin: 0 0 12px 0;
  line-height: 1.4;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: ${props => props.danger ? '#FF3B30' : '#333'};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  
  &:hover {
    background: ${props => props.danger ? '#d12b20' : '#555'};
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: #666;
`;

const EmptyTitle = styled.h3`
  font-size: 18px;
  margin: 0 0 8px 0;
  color: #999;
`;

const EmptyDescription = styled.p`
  font-size: 14px;
  margin: 0 0 20px 0;
  color: #666;
`;

const CreateButton = styled.button`
  background: #17A2B8;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background: #138496;
  }
`;

const MyListings = () => {
    const navigate = useNavigate();
    
    // Mock listings data
    const [listings, setListings] = useState([
        {
            id: 1,
            title: "Professional Recording Studio",
            category: "Studio Services",
            description: "Full-service recording studio with vintage Neve console and Pro Tools HD",
            price: "$150/hour",
            active: true,
            views: 24,
            inquiries: 3
        },
        {
            id: 2,
            title: "Fender Stratocaster 1995",
            category: "Music Equipment",
            description: "Excellent condition American-made Stratocaster with original case",
            price: "$1,200",
            active: true,
            views: 18,
            inquiries: 1
        },
        {
            id: 3,
            title: "Event Photography Package",
            category: "Photography",
            description: "Professional concert and event photography services",
            price: "$500/event",
            active: false,
            views: 12,
            inquiries: 0
        }
    ]);

    const handleBackClick = () => {
        navigate('/catalog');
    };

    const handleEditListing = (listingId) => {
        // Navigate to edit listing page (would be implemented)
        console.log('Edit listing:', listingId);
    };

    const handleDeleteListing = (listingId) => {
        if (window.confirm('Are you sure you want to delete this listing?')) {
            setListings(prev => prev.filter(listing => listing.id !== listingId));
        }
    };

    const handleToggleStatus = (listingId) => {
        setListings(prev => prev.map(listing => 
            listing.id === listingId 
                ? { ...listing, active: !listing.active }
                : listing
        ));
    };

    const handleCreateNewListing = () => {
        navigate('/catalog/create-listing');
    };

    return (
        <Container>
            <Header>
                <BackButton onClick={handleBackClick}>← Back</BackButton>
                <Title>My Listings</Title>
                <div></div>
            </Header>
            
            <Content>
                {listings.length === 0 ? (
                    <EmptyState>
                        <EmptyTitle>No listings yet</EmptyTitle>
                        <EmptyDescription>
                            Create your first listing to start sharing your items or services
                        </EmptyDescription>
                        <CreateButton onClick={handleCreateNewListing}>
                            Create First Listing
                        </CreateButton>
                    </EmptyState>
                ) : (
                    <>
                        {listings.map((listing) => (
                            <ListingCard key={listing.id}>
                                <ListingHeader>
                                    <ListingTitle>{listing.title}</ListingTitle>
                                    <StatusBadge active={listing.active}>
                                        {listing.active ? 'Active' : 'Inactive'}
                                    </StatusBadge>
                                </ListingHeader>
                                
                                <ListingMeta>
                                    <Category>{listing.category}</Category>
                                    <Price>{listing.price}</Price>
                                </ListingMeta>
                                
                                <Description>{listing.description}</Description>
                                
                                <ActionButtons>
                                    <ActionButton onClick={() => handleEditListing(listing.id)}>
                                        Edit
                                    </ActionButton>
                                    <ActionButton onClick={() => handleToggleStatus(listing.id)}>
                                        {listing.active ? 'Deactivate' : 'Activate'}
                                    </ActionButton>
                                    <ActionButton 
                                        danger 
                                        onClick={() => handleDeleteListing(listing.id)}
                                    >
                                        Delete
                                    </ActionButton>
                                </ActionButtons>
                            </ListingCard>
                        ))}
                    </>
                )}
            </Content>
        </Container>
    );
};

export default MyListings;